import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

function STLViewer({
  stlUrl,
  modelUrl,
  format = 'auto',
  width = 400,
  height = 300,
  showRainbowBorder = true,
  borderRadius = 8,
  showShadow = true,
}) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const cameraRef = useRef(null);
  const borderAnimationRef = useRef(null);
  const widthPropRef = useRef(width);
  const heightPropRef = useRef(height);

  const resizeRendererToContainer = useCallback(() => {
    const containerEl = mountRef.current;
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    if (!containerEl || !renderer || !camera) {
      return;
    }

    const rect = containerEl.getBoundingClientRect();
    let containerWidth = containerEl.clientWidth || rect.width || 0;
    let containerHeight = containerEl.clientHeight || rect.height || 0;

    if (!containerWidth || containerWidth <= 1) {
      const fallbackWidth =
        typeof widthPropRef.current === 'number' ? widthPropRef.current : 300;
      const viewportWidth =
        Math.max(document.documentElement?.clientWidth || 0, window.innerWidth || 0) ||
        fallbackWidth;
      containerEl.style.width = `${viewportWidth}px`;

      let current = containerEl;
      while (!containerWidth && current) {
        containerWidth = current.clientWidth || current.getBoundingClientRect()?.width || 0;
        current = current.parentElement;
      }

      containerWidth = containerWidth || viewportWidth || fallbackWidth;
    }

    if (!containerHeight || containerHeight <= 1) {
      const fallbackHeight =
        typeof heightPropRef.current === 'number'
          ? heightPropRef.current
          : containerEl.parentElement?.clientHeight || 300;
      containerHeight = fallbackHeight;
    }

    const newWidth = Math.max(1, Math.floor(containerWidth));
    const newHeight = Math.max(1, Math.floor(containerHeight));

    camera.aspect = newWidth / newHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(newWidth, newHeight);

    if (renderer.domElement) {
      renderer.domElement.style.width = `${newWidth}px`;
      renderer.domElement.style.height = `${newHeight}px`;
      renderer.domElement.style.display = 'block';
    }
  }, []);

  useEffect(() => {
    widthPropRef.current = width;
    heightPropRef.current = height;
    resizeRendererToContainer();
  }, [width, height, resizeRendererToContainer]);

  const resolveModelFormat = (url) => {
    if (format && format !== 'auto') {
      return format.toUpperCase();
    }
    if (!url || typeof url !== 'string') return 'STL';

    try {
      const normalized = url.split('?')[0].split('#')[0].trim().toLowerCase();
      if (normalized.endsWith('.obj')) return 'OBJ';
      if (normalized.endsWith('.stl')) return 'STL';
    } catch (error) {
      console.warn('Failed to auto-detect model format, defaulting to STL:', error);
    }

    return 'STL';
  };

  useEffect(() => {
    const activeUrl = modelUrl || stlUrl;
    if (!mountRef.current || !activeUrl) return;

    // Prepare container and loading overlay
    const container = mountRef.current;
    const initialWidth = typeof widthPropRef.current === 'number' ? widthPropRef.current : 300;
    const initialHeight =
      typeof heightPropRef.current === 'number' ? heightPropRef.current : 300;
    container.innerHTML = '';
    const loadingDiv = document.createElement('div');
    loadingDiv.style.cssText = `
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f8f8;
      color: #666;
      font-family: Arial, sans-serif;
      text-align: center;
      padding: 20px;
      box-sizing: border-box;
    `;
    loadingDiv.innerHTML = `
      <div>
        <div style="font-size: 24px; margin-bottom: 10px;">⏳</div>
        <div>Loading 3D model...</div>
      </div>
    `;
    container.appendChild(loadingDiv);

    // Scene setup
    const scene = new THREE.Scene();

    // Scene setup
    scene.background = new THREE.Color(0xf0f0f0); // Dark blue-gray background
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, initialWidth / initialHeight, 0.1, 1000);
    camera.position.set(0, 0, 100);
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(initialWidth, initialHeight);
    renderer.setClearColor(0xf0f0f0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Add lights - brighter but less glossy appearance
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0); // Maximum ambient light for whiter appearance
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.1); // Slightly brighter
    directionalLight.position.set(50, 50, 50);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const pointLight = new THREE.PointLight(0xffffff, 0.7); // Slightly brighter
    pointLight.position.set(-50, -50, -50);
    scene.add(pointLight);

    // Add additional lights for better illumination
    const topLight = new THREE.DirectionalLight(0xffffff, 0.6); // Slightly brighter
    topLight.position.set(0, 100, 0);
    scene.add(topLight);

    const bottomLight = new THREE.DirectionalLight(0xffffff, 0.5); // Slightly brighter
    bottomLight.position.set(0, -100, 0);
    scene.add(bottomLight);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 2;
    controlsRef.current = controls;

    const detectedFormat = resolveModelFormat(activeUrl);
    const loader = detectedFormat === 'OBJ' ? new OBJLoader() : new STLLoader();
    console.log(`Loading ${detectedFormat} file from URL:`, activeUrl);

    // Add timeout to detect if loading is stuck
    const loadingTimeout = setTimeout(() => {
      console.error(`${detectedFormat} loading timeout - file may be too large or path incorrect`);
      if (loadingDiv && container.contains(loadingDiv)) {
        loadingDiv.innerHTML = `
          <div>
            <div style="font-size: 24px; margin-bottom: 10px;">⏰</div>
            <div>Loading timeout</div>
            <div style="font-size: 12px; margin-top: 5px;">File may be too large or path incorrect</div>
          </div>
        `;
      }
    }, 10000); // 10 second timeout

    let loadedObject = null;

    loader.load(
      activeUrl,
      (loadedData) => {
        clearTimeout(loadingTimeout);
        console.log(`${detectedFormat} file loaded successfully`);
        // Remove loading overlay on success
        if (loadingDiv && container.contains(loadingDiv)) {
          container.removeChild(loadingDiv);
        }
        let objectToRender;

        if (detectedFormat === 'OBJ') {
          objectToRender = loadedData;
          objectToRender.traverse((child) => {
            if (child.isMesh) {
              child.material = new THREE.MeshPhongMaterial({
                color: 0xffffff,
                specular: 0x111111,
                shininess: 10,
                flatShading: true,
                emissive: 0x222222,
                emissiveIntensity: 0.2,
              });
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });
        } else {
          const geometry = loadedData;
          geometry.computeBoundingBox();
          const center = new THREE.Vector3();
          geometry.boundingBox.getCenter(center);
          geometry.translate(-center.x, -center.y, -center.z);

          const material = new THREE.MeshPhongMaterial({
            color: 0xffffff,
            specular: 0x111111,
            shininess: 10,
            flatShading: true,
            emissive: 0x222222,
            emissiveIntensity: 0.2,
          });

          objectToRender = new THREE.Mesh(geometry, material);
          objectToRender.castShadow = true;
          objectToRender.receiveShadow = true;
        }

        const modelGroup = new THREE.Group();
        modelGroup.add(objectToRender);
        scene.add(modelGroup);
        loadedObject = modelGroup;

        modelGroup.position.set(0, 0, 0);
        const computeBox = () => new THREE.Box3().setFromObject(modelGroup);
        const box = computeBox();
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        modelGroup.position.set(-center.x, -center.y, -center.z);

        const safeDimension = (value) => (value === 0 ? 1 : value);
        const heightScale = (initialHeight * 1.2) / safeDimension(size.y);
        const widthScale = (initialWidth * 1.2) / safeDimension(size.x);
        const depthScale = (initialHeight * 1.2) / safeDimension(size.z);
        const scale = Math.min(heightScale, widthScale, depthScale);
        if (Number.isFinite(scale) && scale > 0) {
          modelGroup.scale.setScalar(scale);
        }

        // Recentre after scaling so the model's midpoint stays at the origin
        modelGroup.position.set(0, 0, 0);
        const scaledBox = computeBox();
        const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
        modelGroup.position.set(-scaledCenter.x, -scaledCenter.y, -scaledCenter.z);

        const cameraDistance = initialHeight;
        camera.position.set(cameraDistance * 0.8, cameraDistance * 0.3, cameraDistance * 0.8);
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();
      },
      (progress) => {
        if (progress?.total) {
          const percent = ((progress.loaded / progress.total) * 100).toFixed(2);
          console.log('Loading progress:', `${percent}%`);
        } else {
          console.log('Loading progress:', `${progress.loaded} bytes`);
        }
      },
      (error) => {
        clearTimeout(loadingTimeout);
        console.error(`Error loading ${detectedFormat} file:`, error);
        console.error(`${detectedFormat} URL was:`, activeUrl);
        // Update overlay to show error
        if (loadingDiv && container.contains(loadingDiv)) {
          loadingDiv.innerHTML = `
            <div>
              <div style="font-size: 24px; margin-bottom: 10px;">⚠️</div>
              <div>Failed to load 3D model</div>
              <div style="font-size: 12px; margin-top: 5px;">URL: ${activeUrl}</div>
              <div style="font-size: 12px; margin-top: 5px;">Error: ${error.message || 'Unknown error'}</div>
            </div>
          `;
        }
      }
    );

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle container-based resize (more reliable on mobile)
    // Mount renderer before first resize to allow layout to settle
    container.appendChild(renderer.domElement);

    // Observe container size changes (covers orientation and layout changes)
    const ro = new ResizeObserver(() => resizeRendererToContainer());
    ro.observe(container);
    window.addEventListener('resize', resizeRendererToContainer);
    window.addEventListener('orientationchange', resizeRendererToContainer);
    // Defer first resize to next frame to avoid 0-size on initial mount
    requestAnimationFrame(resizeRendererToContainer);
    // And once more after layout settles on mobile
    setTimeout(resizeRendererToContainer, 200);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeRendererToContainer);
      window.removeEventListener('orientationchange', resizeRendererToContainer);
      if (ro) {
        ro.disconnect();
      }
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      if (loadingDiv && container && container.contains(loadingDiv)) {
        container.removeChild(loadingDiv);
      }
      if (loadedObject && scene) {
        scene.remove(loadedObject);
      }
      if (renderer) {
        renderer.dispose();
      }
      if (controls) {
        controls.dispose();
      }
      cameraRef.current = null;
    };
  }, [stlUrl, modelUrl, format, resizeRendererToContainer]);

  // Rainbow border effect (optional)
  useEffect(() => {
    if (!mountRef.current || !showRainbowBorder) return;

    const container = mountRef.current;
    let animationId;
    let hue = 0;

    const animateRainbow = () => {
      const gradient = `linear-gradient(45deg, 
        hsl(${hue}, 100%, 50%), 
        hsl(${(hue + 60) % 360}, 100%, 50%), 
        hsl(${(hue + 120) % 360}, 100%, 50%), 
        hsl(${(hue + 180) % 360}, 100%, 50%), 
        hsl(${(hue + 240) % 360}, 100%, 50%), 
        hsl(${(hue + 300) % 360}, 100%, 50%)
      )`;

      container.style.border = '3px solid transparent';
      container.style.background = `linear-gradient(white, white) padding-box, ${gradient} border-box`;
      container.style.backgroundClip = 'padding-box, border-box';

      hue = (hue + 0.5) % 360;
      animationId = requestAnimationFrame(animateRainbow);
    };

    animateRainbow();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [showRainbowBorder]);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'relative',
        width: '100%',
        height: height,
        border: showRainbowBorder ? '3px solid transparent' : 'none',
        borderRadius: `${borderRadius}px`,
        overflow: 'hidden',
        boxShadow: showShadow ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
        background: showRainbowBorder
          ? 'linear-gradient(white, white) padding-box, linear-gradient(45deg, red, orange, yellow, green, blue, indigo, violet) border-box'
          : '#f0f0f0',
        backgroundClip: showRainbowBorder ? 'padding-box, border-box' : 'initial',
      }}
    />
  );
}

export default STLViewer;
